import { IconButton, Text } from "@stone-payments/jade";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/utils/classnames";

type QuantitySelectorLoadingProps =
  | {
      loading: true;
      quantity?: number;
      onIncrease?: () => void;
      onDecrease?: () => void;
    }
  | {
      loading?: false;
      quantity: number;
      onIncrease: () => void;
      onDecrease: () => void;
    };

type QuantitySelectorProps = QuantitySelectorLoadingProps & {
  disabled?: boolean;
  removable?: boolean;
  isSeparateBtn?: boolean;
  variant?: "neutral-ghost" | "neutral-subtle";
  size?: "small" | "large";
};

export const QuantitySelector = ({
  quantity,
  onIncrease,
  onDecrease,
  size = "large",
  loading = false,
  removable = false,
  isSeparateBtn = false,
  variant = "neutral-subtle",
  disabled = false,
}: QuantitySelectorProps) => {
  if (loading)
    return <Skeleton className="rounded-sm" height="h-12" width="w-32" />;

  return (
    <div
      className={cn("flex transition-transform duration-200 ease-in-out", {
        "gap-jade-50": isSeparateBtn,
      })}
    >
      {removable && quantity! <= 1 ? (
        <IconButton
          icon="trash"
          aria-label="Remover item"
          data-testid="remove-item-button"
          className={cn({
            "rounded-tr-none! rounded-br-none!": !isSeparateBtn,
          })}
          variant={variant}
          onClick={onDecrease}
          size={size}
          disabled={disabled}
        />
      ) : (
        <IconButton
          icon="minus"
          aria-label="Diminuir quantidade"
          data-testid="decrease-item-button"
          className={cn({
            "rounded-tr-none! rounded-br-none!": !isSeparateBtn,
          })}
          disabled={disabled || (!removable && quantity! <= 1)}
          variant={variant}
          onClick={onDecrease}
          size={size}
        />
      )}

      <div
        className={cn("flex items-center justify-center", {
          "bg-transparent": variant === "neutral-ghost",
          "bg-jade-background-neutral-subtle": variant === "neutral-subtle",
          "max-h-8 min-w-8 rounded-lg bg-jade-background-neutral-subtle":
            isSeparateBtn,
          "p-jade-100!": size === "small",
          "p-jade-150!": size === "large",
        })}
      >
        <Text color={disabled ? "low" : "high"} weight="semibold">
          {quantity}
        </Text>
      </div>
      <IconButton
        icon="plus"
        aria-label="Aumentar quantidade"
        data-testid="increase-item-button"
        className={cn({
          "rounded-tl-none! rounded-bl-none!": !isSeparateBtn,
        })}
        variant={variant}
        onClick={onIncrease}
        size={size}
        disabled={disabled}
      />
    </div>
  );
};
